import { useState } from "react";
import { Service, TenantBusiness, ServiceSellPrice } from "@turizi/shared";
import { ServiceTitle, ServiceTitleStatic } from "./services/service-title";
import { applyDiscount, formatDayShort, formatPrice, todayStart } from "@/lib/format-utils";
import { TagIcon } from "@/lib/tag-utils";
import { useTranslation } from "react-i18next";
import { Tooltip, ActionIcon } from "@mantine/core";
import { IconPin, IconChevronDown, IconChevronRight, IconCalendar, IconBuildingStore } from "@tabler/icons-react";
import dayjs from "dayjs";

// const isActiveNow = (item: ServiceSellPrice) => {
//     const today = todayStart();
//     if (!item.start_day && !item.end_day) return true;
//     if (!item.start_day) return item.end_day ? item.end_day >= today : true;
//     if (!item.end_day) return item.start_day <= today;
//     return item.start_day <= today && item.end_day >= today;
// };

// const isPast = (date: string | null | undefined) => {
//     if (!date) return false;
//     const today = todayStart();
//     return dayjs(date).isBefore(today, 'day');
// };

// const isUpcoming = (startDate: string | null | undefined) => {
//     if (!startDate) return false;
//     const today = todayStart();
//     return dayjs(startDate).isAfter(today, 'day');
// };

interface Props {
    service: Service;
    onSelect?: (service: Service, pricePeriod: ServiceSellPrice, tierKey: string) => void;
    client?: TenantBusiness;
    pinnedIds?: string[];
    className?: string;
}

export default function ServiceTariffCard({
    service,
    onSelect,
    client,
    pinnedIds = [],
    className,
}: Props) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const getDiscountPercentage = (currency: string): number | null => {
        const clientDiscountCategory = client?.discount_category;
        if (!clientDiscountCategory || !service.discounts) {
            return null;
        }
        const discountCategory = service.discounts.find(
            discount => discount.category === clientDiscountCategory
        );
        if (!discountCategory || !discountCategory.discounts.length) {
            return null;
        }
        return discountCategory.discounts.find(discount => discount.currency === currency)?.percentage || null;
    };

    const sortedPrices = [...(service.sell_prices || [])].sort((a, b) => {
        const dateCompare = (a.start_day || '').localeCompare(b.start_day || '');
        if (dateCompare !== 0) return dateCompare;
        if (!a.business_id && b.business_id) return -1;
        if (a.business_id && !b.business_id) return 1;
        return 0;
    });

    const hasSpecificPrices = service.sell_prices?.some(p => Boolean(p.business_id));

    return (
        <div
            key={service.id}
            onClick={() => setExpanded(prev => !prev)}
            className={`
                ${className || 'solid-box-border-hover'}
                flex flex-col gap-3 cursor-pointer select-none transition-all
            `}
        >
            {/* Header / Main card row */}
            <div className="flex items-start justify-between gap-4 w-full">
                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    {/* Category Icon and ID */}
                    <div className="flex flex-col items-center gap-1.5 p-1 shrink-0">
                        <TagIcon
                            name={service.category}
                            hideText
                            size="xs"
                            noBackground
                        />
                        <div className="text-gray-400 text-[10px] text-center ">
                            <span className="opacity-40 text-[9px]">#</span>{service.id}
                        </div>
                    </div>

                    {/* Name and Subheader */}
                    <div className="flex-1 min-w-0 pt-0.5">
                        <div className="text-sm">
                            <ServiceTitle service={service} />
                        </div>
                        {service.child_services && service.child_services.length > 0 && (
                            <div className="space-y-2 mt-2 bg-gray-50 p-2 rounded-lg">
                                {service.child_services.map(child => (
                                    <div key={child.id} className="text-gray-800 text-xs flex items-start gap-1">
                                        <TagIcon name={child.category} hideText size="xs" />
                                        <div>
                                            {ServiceTitleStatic(child)} <span className="text-gray-400 text-[10px]">#{child.id}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right side: Specific prices hint & Expand indicator */}
                <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                    {sortedPrices.length > 0 && (
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="md"
                            radius="md"
                            onClick={() => setExpanded(prev => !prev)}
                        >
                            {expanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                        </ActionIcon>
                    )}
                </div>
            </div>

            {/* Collapsible expanded section showing all prices */}
            {expanded && sortedPrices.length > 0 && (
                <div className="mt-2" onClick={e => e.stopPropagation()}>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                        {sortedPrices.map((p, pIdx) => {
                            const business = p.business || (p.business_id ? service.providers?.find(pr => pr.id === p.business_id) : null);
                            const isSpecificProvider = Boolean(p.business_id);
                            const priceTiers = [
                                { key: 'default', label: t('general'), amount: p.default_amount, currency: p.default_currency, isPrimary: true },
                                { key: 'foreigner', label: t('foreigner'), amount: p.foreigner_amount, currency: p.foreigner_currency },
                                { key: 'child', label: t('children'), amount: p.child_amount, currency: p.child_currency },
                                { key: 'senior', label: service.show_retired ? t('retired_plural') : t('seniors'), amount: p.senior_amount, currency: p.senior_currency },
                                { key: 'infant', label: t('infants'), amount: p.infant_amount, currency: p.infant_currency }
                            ].filter((tier): tier is { key: string; label: string; amount: number; currency: string } =>
                                tier.amount !== null && tier.amount !== undefined && (tier.isPrimary ? true : tier.amount > 0)
                            );

                            if (priceTiers.length === 0) return null;

                            const dateText = p.start_day || p.end_day
                                ? `${p.start_day ? formatDayShort(p.start_day) : '*'} ${p.end_day ? `- ${formatDayShort(p.end_day)}` : ''}`
                                : t('always_available');

                            return (
                                <div key={p.id || pIdx} className="grid grid-cols-12 items-stretch">
                                    {/* Left side: Date Range & Provider Scope */}
                                    <div className={`col-span-12 md:col-span-4 p-3.5 sm:p-4 flex flex-col justify-center gap-1.5 md:border-b md:border-b-0 md:border-r border-gray-100`}>
                                        {/* Dates & Status Badge */}
                                        <div className="flex items-center gap-1.5 font-bold text-xs md:text-sm text-gray-800">
                                            <IconCalendar size={15} className="text-gray-400" stroke={1.8} />
                                            <span>{dateText}</span>
                                        </div>

                                        {/* Provider Scope Badge */}
                                        <div>
                                            {isSpecificProvider ? (
                                                <span
                                                    className="inline-flex items-center gap-1.5 text-xxs w-full px-1 py-0.5 bg-amber-50 rounded-md border border-amber-100 text-amber-600"
                                                    title={business?.name ? `${business.name}${business.city ? ` (${business.city})` : ''}` : undefined}
                                                >
                                                    {business?.category ? (
                                                        <TagIcon name={business.category} size="xxs" hideText noBackground />
                                                    ) : (
                                                        <IconBuildingStore size={12} className="text-amber-700 shrink-0" />
                                                    )}
                                                    <span className="font-semibold truncate max-w-[160px]">
                                                        {business?.name || "business_name_missing"}
                                                    </span>
                                                </span>
                                            ) : (
                                                null
                                            )}
                                        </div>
                                    </div>

                                    {/* Right side: Price Tiers stack */}
                                    <div className="col-span-12 md:col-span-8 divide-y divide-gray-100">
                                        {priceTiers.map((tier, tIdx) => {
                                            const discount = getDiscountPercentage(tier.currency);
                                            const finalAmount = applyDiscount(tier.amount, discount);
                                            const itemId = `${service.id}-${p.id}-${tier.key}`;
                                            const isPinned = pinnedIds.includes(itemId);

                                            return (
                                                <div key={tIdx} className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-slate-50/30 transition-colors">
                                                    {/* Tier Label */}
                                                    <div className="col-span-4 text-xs font-semibold text-gray-600">
                                                        {tier.label}
                                                    </div>
                                                    {/* Original Price */}
                                                    <div className="col-span-3 text-right text-xs  text-gray-400">
                                                        {discount ? `$ ${formatPrice(tier.amount)} ${tier.currency}` : ''}
                                                    </div>
                                                    {/* Discount / Separator */}
                                                    <div className="col-span-1 text-center text-xs font-semibold text-indigo-500">
                                                        {discount ? `-${discount}%` : ''}
                                                    </div>
                                                    {/* Final Price */}
                                                    <div className="col-span-3 text-right text-xs  font-bold text-gray-900">
                                                        $ {formatPrice(finalAmount)} {tier.currency}
                                                    </div>
                                                    {/* Pin Button */}
                                                    <div className="col-span-1 text-right">
                                                        {onSelect && (
                                                            <Tooltip label={isPinned ? t('remove_from_simulator') : t('add_to_simulator')} position="top" withArrow>
                                                                <ActionIcon
                                                                    variant={isPinned ? "filled" : "light"}
                                                                    color="indigo"
                                                                    size="xs"
                                                                    radius="md"
                                                                    onClick={() => onSelect(service, p, tier.key)}
                                                                >
                                                                    <IconPin size={12} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
